import { useState, useEffect, useRef, useCallback } from 'react';
import { useAnalytics } from '../../../hooks/useAnalytics';
import jsPDF from 'jspdf';
import {
  getBrowserInfo,
  infoSections,
  getVulnerabilityAssessment,
} from './scanUtils';

export function FloatingCredentials() {
  const [isOpen, setIsOpen] = useState(false);
  const [cliente, setCliente] = useState('');
  const [password, setPassword] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [showIpPanel, setShowIpPanel] = useState(false);
  const [ipLoading, setIpLoading] = useState(false);
  const [visitorInfo, setVisitorInfo] = useState<Record<string, string>>({});
  const [showTimeline, setShowTimeline] = useState(false);
  const [showExploit, setShowExploit] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState<any[]>([]);
  const [vulns, setVulns] = useState<any[]>([]);
  const [exploitLoading, setExploitLoading] = useState(false);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { getAnalyticsReport, clearAnalytics } = useAnalytics();
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  /* -------------------------------------------------------------------------
   * Panel open / close handling
   * ----------------------------------------------------------------------- */
  const closePanel = useCallback(() => {
    setIsOpen(false);
    setShowIpPanel(false);
    setShowTimeline(false);
    setShowExploit(false);
    if (autoCloseTimer.current) {
      clearTimeout(autoCloseTimer.current);
      autoCloseTimer.current = null;
    }
  }, []);

  const openPanel = useCallback(() => {
    setIsOpen(true);
    if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    autoCloseTimer.current = setTimeout(() => {
      setIsOpen(false);
      setShowIpPanel(false);
      setShowTimeline(false);
      setShowExploit(false);
      autoCloseTimer.current = null;
    }, 8000);
  }, []);

  const handleMouseEnter = () => {
    if (autoCloseTimer.current) {
      clearTimeout(autoCloseTimer.current);
      autoCloseTimer.current = null;
    }
  };

  const handleMouseLeave = () => {
    if (isOpen) {
      autoCloseTimer.current = setTimeout(() => {
        setIsOpen(false);
        setShowIpPanel(false);
        setShowTimeline(false);
        setShowExploit(false);
        autoCloseTimer.current = null;
      }, 8000);
    }
  };

  /* -------------------------------------------------------------------------
   * Timeline auto‑refresh
   * ----------------------------------------------------------------------- */
  useEffect(() => {
    const interval = setInterval(() => {
      if (showTimeline) {
        const ad = localStorage.getItem('kalinux_analytics');
        if (ad) {
          const p = JSON.parse(ad);
          setTimelineEvents(p.events || []);
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [showTimeline]);

  const handleIngresar = (e: React.FormEvent) => {
    e.preventDefault();
    setShowMessage(true);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };
  const formatTimestamp = (iso: string) =>
    new Date(iso).toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  const getEventColorClass = (color: string) => {
    const map: Record<string, string> = {
      cyan: 'text-cyan-400 border-cyan-400/30 bg-cyan-400/10',
      green: 'text-green-400 border-green-400/30 bg-green-400/10',
      yellow: 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10',
      purple: 'text-purple-400 border-purple-400/30 bg-purple-400/10',
      rosa: 'text-pink-400 border-pink-400/30 bg-pink-400/10',
    };
    return map[color] || 'text-gray-400 border-gray-400/30 bg-gray-400/10';
  };

  const handleClearTimeline = () => {
    if (
      confirm(
        '¿Estás seguro de que deseas limpiar todo el historial de eventos?'
      )
    ) {
      clearAnalytics();
      setTimelineEvents([]);
      localStorage.removeItem('kalinux_analytics');
      localStorage.removeItem('kalinux_mouse_positions');
    }
  };

  /* -------------------------------------------------------------------------
   * IP / OSINT panel
   * ----------------------------------------------------------------------- */
  const toggleIpPanel = async () => {
    if (showTimeline) setShowTimeline(false);
    if (showExploit) setShowExploit(false);

    if (!showIpPanel && !visitorInfo.ip) {
      setIpLoading(true);
      setShowIpPanel(true);
      const browserInfo = await getBrowserInfo();
      const analyticsReport = getAnalyticsReport();
      const ad = localStorage.getItem('kalinux_analytics');
      const pa = ad ? JSON.parse(ad) : null;

      try {
        const response = await fetch(
          'http://ip-api.com/json/?fields=status,query,country,countryCode,regionName,city,zip,lat,lon,isp,org,as,timezone,mobile,proxy,hosting'
        );
        const data = await response.json();

        if (data.status === 'success') {
          setVisitorInfo({
            ...browserInfo,
            ip: data.query || 'No disponible',
            city: data.city || '',
            country: data.country || '',
            countryCode: data.countryCode || '',
            region: data.regionName || '',
            zip: data.zip || '',
            lat: String(data.lat || ''),
            lon: String(data.lon || ''),
            isp: data.isp || '',
            org: data.org || '',
            asn: data.as || '',
            mobile: data.mobile ? 'Sí' : 'No',
            proxy: data.proxy ? 'Detectado' : 'No',
            hosting: data.hosting ? 'Sí' : 'No',
            latlon:
              data.lat && data.lon ? `${data.lat}, ${data.lon}` : '',
            sessionId: pa?.sessionId || 'N/A',
            sessionStart: pa?.startTime
              ? new Date(pa.startTime).toLocaleString('es-AR')
              : 'N/A',
            totalTimeOnSite: formatTime(analyticsReport.currentTime),
            totalClicks: String(pa?.clicks || 0),
            scrollDepth: `${pa?.scrollDepth || 0}%`,
            interactions: String(pa?.interactions || 0),
            pagesVisited: String(pa?.pageViews?.length || 0),
            referrer: document.referrer || 'Directo',
          });
        } else {
          setVisitorInfo({ ...browserInfo, ip: 'No disponible' });
        }
      } catch (e) {
        // Fallback to a simpler service if ip-api fails
        try {
          const fallback = await fetch('https://api.ipify.org?format=json');
          const d = await fallback.json();
          setVisitorInfo({ ...browserInfo, ip: d.ip || 'No disponible' });
        } catch {
          setVisitorInfo({ ...browserInfo, ip: 'No disponible' });
        }
      } finally {
        setIpLoading(false);
      }
    } else {
      setShowIpPanel((prev) => !prev);
    }
  };

  /* -------------------------------------------------------------------------
   * Timeline panel
   * ----------------------------------------------------------------------- */
  const toggleTimeline = () => {
    if (showIpPanel) setShowIpPanel(false);
    if (showExploit) setShowExploit(false);
    if (!showTimeline) {
      const ad = localStorage.getItem('kalinux_analytics');
      if (ad) {
        const p = JSON.parse(ad);
        setTimelineEvents(p.events || []);
      }
    }
    setShowTimeline(!showTimeline);
  };

  /* -------------------------------------------------------------------------
   * Exploit / vulnerability panel
   * ----------------------------------------------------------------------- */
  const toggleExploit = () => {
    if (showIpPanel) setShowIpPanel(false);
    if (showTimeline) setShowTimeline(false);
    if (!showExploit && vulns.length === 0) {
      setExploitLoading(true);
      setShowExploit(true);
      setTimeout(() => {
        setVulns(getVulnerabilityAssessment());
        setExploitLoading(false);
      }, 1500);
    } else {
      setShowExploit(!showExploit);
    }
  };

  const getSeverityColor = (sev: string) => {
    const map: Record<string, string> = {
      critical:
        'text-red-500 bg-red-500/10 border-red-500/30',
      high: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
      medium: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
      low: 'text-green-400 bg-green-400/10 border-green-400/30',
      info: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
    };
    return map[sev] || map.info;
  };

  const getSeverityLabel = (sev: string) => {
    const map: Record<string, string> = {
      critical: 'CRÍTICO',
      high: 'ALTO',
      medium: 'MEDIO',
      low: 'BAJO',
      info: 'INFO',
    };
    return map[sev] || 'INFO';
  };

  /* -------------------------------------------------------------------------
   * PDF export
   * ----------------------------------------------------------------------- */
  const exportToPDF = async () => {
    setIsExportingPDF(true);
    try {
      const doc = new jsPDF();
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pw - margin * 2;
      let y = margin;

      // Header
      doc.setFillColor(0, 0, 0);
      doc.rect(0, 0, pw, 35, 'F');
      doc.setTextColor(0, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('INFORME SCAN PROFESIONAL', pw / 2, 15, {
        align: 'center',
      });
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.text(
        'STI - Servicio Técnico Informático',
        pw / 2,
        22,
        { align: 'center' }
      );
      const now = new Date();
      doc.setFontSize(8);
      doc.setTextColor(200, 200, 200);
      doc.text(
        `Generado: ${now.toLocaleString('es-AR')}`,
        pw / 2,
        28,
        { align: 'center' }
      );
      y = 45;

      const addSection = (
        title: string,
        items: Array<{ label: string; value: string }>
      ) => {
        if (y > ph - 40) {
          doc.addPage();
          y = margin;
        }
        // Section title background
        doc.setFillColor(20, 20, 20);
        doc.rect(margin, y, contentWidth, 8, 'F');
        doc.setTextColor(0, 255, 255);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin + 3, y + 5.5);
        y += 10;

        // Items
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        items.forEach((item) => {
          if (y > ph - 20) {
            doc.addPage();
            y = margin;
          }
          doc.setTextColor(150, 150, 150);
          doc.text(`${item.label}:`, margin + 2, y);
          doc.setTextColor(255, 255, 255);
          const lines = doc.splitTextToSize(item.value || 'N/A', contentWidth - 50);
          doc.text(lines, margin + 50, y);
          y += lines.length * 4 + 2;
        });
        y += 3;
      };

      // --------------------------------------------------------------------
      // Visitor info sections
      // --------------------------------------------------------------------
      infoSections.forEach((section) => {
        const items = section.items
          .map((item) => {
            let value = '';
            if (item.key === 'latlon') {
              value =
                visitorInfo.lat && visitorInfo.lon
                  ? `${visitorInfo.lat}, ${visitorInfo.lon}`
                  : '';
            } else {
              value = (visitorInfo as any)[item.key] || '';
            }
            return { label: item.label, value };
          })
          .filter((i) => i.value);
        if (items.length) {
          addSection(section.title, items);
        }
      });

      if (visitorInfo.userAgent) {
        addSection('📝 USER AGENT', [
          { label: 'User Agent', value: visitorInfo.userAgent },
        ]);
      }

      // --------------------------------------------------------------------
      // Vulnerability assessment
      // --------------------------------------------------------------------
      if (vulns.length) {
        addSection('🛡️ ANÁLISIS DE VULNERABILIDADES', vulns.map((v) => ({
          label: v.name,
          value: `[${getSeverityLabel(v.severity)}] ${v.status} - ${v.detail}`,
        })));
      }

      // --------------------------------------------------------------------
      // Timeline / activity summary
      // --------------------------------------------------------------------
      const ad = localStorage.getItem('kalinux_analytics');
      const pa = ad ? JSON.parse(ad) : null;
      const events = pa?.events || timelineEvents || [];
      if (events.length) {
        const report = getAnalyticsReport();
        addSection('📊 RESUMEN DE ACTIVIDAD', [
          { label: 'Clics Totales', value: String(report.clicks) },
          { label: 'Scroll Máximo', value: `${report.scrollDepth}%` },
          { label: 'Interacciones', value: String(report.interactions) },
          { label: 'Tiempo en Sitio', value: formatTime(report.currentTime) },
          { label: 'Total Eventos', value: String(events.length) },
        ]);

        // Detailed timeline
        if (y > ph - 40) {
          doc.addPage();
          y = margin;
        }
        doc.setFillColor(20, 20, 20);
        doc.rect(margin, y, contentWidth, 8, 'F');
        doc.setTextColor(180, 130, 255);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('⏱️ TIMELINE DE EVENTOS', margin + 3, y + 5.5);
        y += 10;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);

        events.forEach((event: any) => {
          if (y > ph - 20) {
            doc.addPage();
            y = margin;
          }
          const ts = event.timestamp
            ? new Date(event.timestamp).toLocaleTimeString('es-AR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
            : 'N/A';
          const desc = event.description || 'Evento';
          const typeLabel =
            event.type === 'click'
              ? 'CLIC'
              : event.type === 'scroll'
              ? 'SCROLL'
              : event.type === 'interaction'
              ? 'INPUT'
              : event.type === 'page'
              ? 'PÁGINA'
              : 'OTRO';

          // Color per event type
          if (event.type === 'click') doc.setTextColor(0, 220, 220);
          else if (event.type === 'scroll') doc.setTextColor(0, 220, 100);
          else if (event.type === 'interaction') doc.setTextColor(220, 200, 0);
          else if (event.type === 'page') doc.setTextColor(220, 130, 180);
          else doc.setTextColor(180, 130, 255);

          doc.text(`[${ts}]`, margin + 2, y);
          doc.setTextColor(200, 200, 200);
          doc.text(typeLabel, margin + 28, y);
          doc.setTextColor(255, 255, 255);
          const dl = doc.splitTextToSize(desc, contentWidth - 60);
          doc.text(dl, margin + 48, y);
          y += dl.length * 3.5 + 2;

          // Extra info (text, depth, etc.)
          let extra = '';
          if (event.type === 'click' && event.data?.text) {
            extra = `Texto: ${event.data.text.substring(0, 60)}`;
          } else if (
            event.type === 'scroll' &&
            event.data?.depth !== undefined
          ) {
            extra = `Profundidad: ${event.data.depth}%`;
          } else if (event.type === 'interaction' && event.data?.name) {
            extra = `Campo: ${event.data.name}`;
          }
          if (extra) {
            if (y > ph - 20) {
              doc.addPage();
              y = margin;
            }
            doc.setTextColor(120, 120, 120);
            doc.setFontSize(7);
            doc.text(`  └ ${extra}`, margin + 48, y);
            doc.setFontSize(8);
            y += 3.5;
          }
        });
      }

      // --------------------------------------------------------------------
      // Footer (page numbers)
      // --------------------------------------------------------------------
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFillColor(0, 0, 0);
        doc.rect(0, ph - 15, pw, 15, 'F');
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(7);
        doc.text(
          'Datos obtenidos vía navegador y APIs públicas - Técnicas OSINT y Fingerprinting aplicadas',
          pw / 2,
          ph - 8,
          { align: 'center' }
        );
        doc.setTextColor(0, 255, 255);
        doc.text(
          `STI - Servicio Técnico Informático | Análisis Profesional de Seguridad | Pág ${i}/${totalPages}`,
          pw / 2,
          ph - 4,
          { align: 'center' }
        );
      }

      // Save the PDF
      doc.save(
        `informe-scan-${now
          .getFullYear()
          .toString()}${String(now.getMonth() + 1).padStart(2, '0')}${String(
          now.getDate()
        ).padStart(2, '0')}-${String(now.getHours()).padStart(
          2,
          '0'
        )}${String(now.getMinutes()).padStart(2, '0')}.pdf`
      );
    } catch (err) {
      console.error('Error generando PDF:', err);
    } finally {
      setIsExportingPDF(false);
    }
  };

  const anyPanelOpen = showIpPanel || showTimeline || showExploit;

  return (
    <>
      {/* Floating button (left) */}
      <div className="fixed left-0 top-1/2 -translate-y-1/2 z-50 flex flex-col items-start">
        <button
          onClick={() => {
            if (isOpen) closePanel();
            else openPanel();
          }}
          className={`flex items-center gap-2 py-3 px-2 rounded-r-xl border border-l-0 transition-all duration-500 cursor-pointer whitespace-nowrap group ${
            isOpen
              ? 'bg-pink-500 border-pink-400 shadow-lg shadow-pink-400/30'
              : 'bg-gray-900/90 backdrop-blur-sm border-gray-700 hover:bg-gray-800 hover:border-pink-400/50 hover:shadow-lg hover:shadow-pink-400/10'
          }`}
          aria-label="Abrir panel de credenciales"
        >
          <div className="flex flex-col items-center gap-1">
            <i
              className={`ri-user-line text-lg transition-colors duration-300 ${
                isOpen ? 'text-white' : 'text-pink-400 group-hover:text-pink-300'
              }`}
            ></i>
            <span
              className={`text-[10px] font-bold tracking-wider transition-colors duration-300 ${
                isOpen ? 'text-white' : 'text-gray-400 group-hover:text-pink-300'
              }`}
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            >
              ACCESO
            </span>
          </div>
        </button>
      </div>

      {/* Main credential panel (right) */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`fixed top-0 h-full z-50 bg-gray-950 border-l border-gray-800 shadow-2xl transition-all duration-500 ease-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ right: 0, width: '340px', maxWidth: '90vw' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-pink-400/10 border border-pink-400/20 overflow-hidden">
              <img
                src="https://static.readdy.ai/image/5390af1b9222ae6e92dc7da25193e2e6/65a0e09d2eafecea725f43c7f76a00d5.png"
                alt="STI Logo"
                title="STI - Servicio Técnico Informático"
                className="w-8 h-8 object-contain"
              />
            </div>
            <h3 className="text-white font-bold text-sm">Area Kali Linux</h3>
          </div>
          <button
            onClick={closePanel}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-pink-400 hover:bg-gray-800 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-4 py-5">
          {/* Header logo */}
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-pink-400/20 to-cyan-400/20 border border-pink-400/30 shadow-lg shadow-pink-400/10 overflow-hidden">
              <img
                src="https://static.readdy.ai/image/5390af1b9222ae6e92dc7da25193e2e6/65a0e09d2eafecea725f43c7f76a00d5.png"
                alt="STI Logo"
                title="STI - Servicio Técnico Informático"
                className="w-12 h-12 object-contain"
              />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-5">
            <h2 className="text-lg font-bold text-white mb-1">
              Identifíquese con sus{' '}
              <span className="text-cyan-400">credenciales</span>
            </h2>
            <div className="w-12 h-0.5 bg-gradient-to-r from-pink-400 to-cyan-400 mx-auto mt-2 rounded-full"></div>
          </div>

          {/* Login form */}
          <form onSubmit={handleIngresar}>
            <div className="mb-3">
              <label
                htmlFor="floating-cliente"
                className="flex items-center gap-2 text-xs font-medium text-gray-300 mb-1.5"
              >
                <span>Area Tecnica</span>
                <img
                  src="https://static.readdy.ai/image/5390af1b9222ae6e92dc7da25193e2e6/9c10d7f2d6d988aad012958f659b9dc5.png"
                  alt="Kali Linux Logo"
                  title="Kali Linux"
                  className="w-4 h-4 object-contain"
                />
              </label>
              <input
                id="floating-cliente"
                type="text"
                value={cliente}
                onChange={(e) => {
                  setCliente(e.target.value);
                  setShowMessage(false);
                }}
                placeholder="Ingrese su usuario"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-pink-400 focus:ring-1 focus:ring-pink-400 transition-all duration-300"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="floating-password"
                className="block text-xs font-medium text-gray-300 mb-1.5"
              >
                Contraseña
              </label>
              <input
                id="floating-password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setShowMessage(false);
                }}
                placeholder="Ingrese su contraseña"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-pink-400 focus:ring-1 focus:ring-pink-400 transition-all duration-300"
              />
            </div>
            <button
              type="submit"
              className="w-full py-2.5 bg-pink-500 text-white text-sm font-semibold rounded-lg hover:bg-pink-400 transition-all shadow-lg shadow-pink-500/20 hover:shadow-pink-400/30 whitespace-nowrap cursor-pointer"
            >
              Ingresar
            </button>

            {/* Guest download link */}
            <div className="mt-3 pt-3 border-t border-gray-800">
              <a
                href="https://www.dropbox.com/scl/fo/m5aevfo1eak4igpady2xc/AKn75-0DCtNgzjKopcADcJE?rlkey=kdi1ckhkmr4exsf8qx4dgtb3t&dl=0"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors duration-300 cursor-pointer"
              >
                <i className="ri-download-2-line text-sm"></i>
                <span>Ingreso descargas como Invitado</span>
                <i className="ri-external-link-line text-sm"></i>
              </a>
            </div>

            {/* Message after submit */}
            {showMessage && (
              <div className="mt-3 text-center animate-fade-in">
                <a
                  href="https://wa.me/5491140402887?text=Hola%2C%20necesito%20la%20clave%20de%20acceso%20al%20sistema"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center gap-1.5 text-xs text-green-400 hover:text-green-300 transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-base"></i>
                  <span>Consulte Clave al Admin</span>
                  <i className="ri-external-link-line text-xs"></i>
                </a>
              </div>
            )}
          </form>

          {/* Footer note */}
          <div className="mt-5 pt-4 border-t border-gray-800">
            <div className="flex items-center gap-2 text-gray-500 text-[11px]">
              <i className="ri-lock-line text-cyan-400"></i>
              <span>Conexión segura y encriptada</span>
            </div>
          </div>

          {/* Action buttons: SCAN, + INFO, EXPLOIT */}
          <div className="mt-4 pt-4 border-t border-gray-800">
            <div className="flex gap-1.5">
              <button
                onClick={toggleIpPanel}
                className={`flex-1 py-1.5 rounded-lg border transition-all duration-300 cursor-pointer whitespace-nowrap flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold ${
                  showIpPanel
                    ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-400 shadow-lg shadow-cyan-400/10'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-cyan-400 hover:border-cyan-400/40 hover:bg-gray-800/80'
                }`}
                title="Análisis Profesional - Escaneo OSINT"
              >
                <i className={`ri-spy-line text-sm ${showIpPanel ? 'animate-pulse' : ''}`}></i>
                <span>SCAN</span>
              </button>
              <button
                onClick={toggleTimeline}
                className={`flex-1 py-1.5 rounded-lg border transition-all duration-300 cursor-pointer whitespace-nowrap flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold ${
                  showTimeline
                    ? 'bg-purple-500/20 border-purple-400/50 text-purple-400 shadow-lg shadow-purple-400/10'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-purple-400 hover:border-purple-400/40 hover:bg-gray-800/80'
                }`}
                title="Historial de Eventos"
              >
                <i className={`ri-time-line text-sm ${showTimeline ? 'animate-pulse' : ''}`}></i>
                <span>+ INFO</span>
              </button>
              <button
                onClick={toggleExploit}
                className={`flex-1 py-1.5 rounded-lg border transition-all duration-300 cursor-pointer whitespace-nowrap flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold ${
                  showExploit
                    ? 'bg-red-500/20 border-red-400/50 text-red-400 shadow-lg shadow-red-400/10'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-red-400 hover:border-red-400/40 hover:bg-gray-800/80'
                }`}
                title="Análisis de Vulnerabilidades - Hacking Ético"
              >
                <i className={`ri-shield-keyhole-line text-sm ${showExploit ? 'animate-pulse' : ''}`}></i>
                <span>EXPLOIT</span>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom progress bar when panel is open but no sub‑panel */}
        {isOpen && !anyPanelOpen && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-pink-400 to-cyan-400 animate-shrink-bar"
              style={{ animationDuration: '8s' }}
            ></div>
          </div>
        )}
      </div>

      {/* IP/SCAN panel */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`fixed top-0 h-full z-40 bg-gray-950 border-l border-gray-800 shadow-2xl transition-all duration-500 ease-out overflow-y-auto ${
          isOpen && showIpPanel
            ? 'translate-x-0 opacity-100'
            : 'translate-x-full opacity-0 pointer-events-none'
        }`}
        style={{ right: '340px', width: '420px', maxWidth: '50vw' }}
      >
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-950 z-10">
          <div className="flex items-center gap-2">
            <i className="ri-spy-line text-cyan-400 text-base animate-pulse"></i>
            <div>
              <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider block">
                Análisis SCAN Profesional
              </span>
              <span className="text-[9px] text-gray-500">
                Recopilación Avanzada de Información
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportToPDF}
              disabled={isExportingPDF || !visitorInfo.ip}
              className={`px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                isExportingPDF || !visitorInfo.ip
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-pink-400/20 text-pink-400 hover:bg-pink-400/30 border border-pink-400/30'
              }`}
              title="Exportar informe completo en PDF"
            >
              {isExportingPDF ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Generando...</span>
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-line"></i>
                  <span>PDF</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowIpPanel(false)}
              className="text-gray-600 hover:text-gray-400 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>
        <div className="p-4">
          {ipLoading ? (
            <div className="flex flex-col items-center gap-3 py-16 justify-center">
              <div className="relative">
                <div className="w-14 h-14 border-4 border-cyan-400/20 border-t-cyan-400 rounded-full animate-spin"></div>
                <i className="ri-spy-line text-cyan-400 text-2xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></i>
              </div>
              <div className="text-center">
                <span className="text-sm text-cyan-400 font-semibold block">
                  Escaneando objetivo...
                </span>
                <span className="text-xs text-gray-500">
                  Recopilando datos del visitante
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {infoSections.map((section) => {
                const hasData = section.items.some((item) => {
                  if (item.key === 'latlon')
                    return visitorInfo.lat && visitorInfo.lon;
                  return (visitorInfo as any)[item.key];
                });
                if (!hasData) return null;
                return (
                  <div
                    key={section.title}
                    className="bg-gray-900/80 rounded-lg p-3 border border-gray-800/80"
                  >
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-800">
                      <i className={`${section.icon} ${section.color} text-sm`}></i>
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {section.title}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {section.items.map((row) => {
                        let value = '';
                        if (row.key === 'latlon') {
                          if (visitorInfo.lat && visitorInfo.lon)
                            value = `${visitorInfo.lat}, ${visitorInfo.lon}`;
                        } else {
                          value = (visitorInfo as any)[row.key] || '';
                        }
                        if (!value) return null;
                        return (
                          <div
                            key={row.key}
                            className="flex items-center gap-2 py-0.5 hover:bg-gray-800/50 rounded px-1 transition-colors"
                          >
                            <i className={`${row.icon} ${row.color} text-xs flex-shrink-0`}></i>
                            <span className="text-[10px] text-gray-400 w-24 flex-shrink-0">
                              {row.label}
                            </span>
                            <span
                              className={`text-xs text-gray-200 truncate flex-1 ${
                                row.key === 'ip'
                                  ? 'font-mono font-bold text-cyan-400'
                                  : ''
                              }`}
                            >
                              {value}
                            </span>
                            {row.key === 'ip' && (
                              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse ml-1 flex-shrink-0"></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {visitorInfo.userAgent && (
                <div className="bg-gray-900/80 rounded-lg p-3 border border-gray-800/80">
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-800">
                    <i className="ri-file-text-line text-gray-400 text-sm"></i>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      📝 USER AGENT COMPLETO
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-400 break-all leading-relaxed">
                    {visitorInfo.userAgent}
                  </p>
                </div>
              )}

              <div className="pt-2 mt-2 border-t border-gray-800">
                <div className="flex items-center gap-2 text-[10px] text-gray-600">
                  <i className="ri-shield-check-line text-green-500"></i>
                  <span>
                    Análisis completado • Datos vía navegador y APIs públicas
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-cyan-400 mt-1">
                  <i className="ri-spy-line"></i>
                  <span>Técnicas de OSINT y Fingerprinting aplicadas</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline panel */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`fixed top-0 h-full z-40 bg-gray-950 border-l border-gray-800 shadow-2xl transition-all duration-500 ease-out overflow-y-auto ${
          isOpen && showTimeline
            ? 'translate-x-0 opacity-100'
            : 'translate-x-full opacity-0 pointer-events-none'
        }`}
        style={{ right: '340px', width: '420px', maxWidth: '50vw' }}
      >
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-950 z-10">
          <div className="flex items-center gap-2">
            <i className="ri-time-line text-purple-400 text-base animate-pulse"></i>
            <div>
              <span className="text-xs font-bold text-purple-400 uppercase tracking-wider block">
                Timeline de Eventos
              </span>
              <span className="text-[9px] text-gray-500">
                Historial de Actividad en Tiempo Real
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportToPDF}
              disabled={isExportingPDF}
              className={`px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                isExportingPDF
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-purple-400/20 text-purple-400 hover:bg-purple-400/30 border border-purple-400/30'
              }`}
              title="Exportar informe completo + timeline en PDF"
            >
              {isExportingPDF ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Generando...</span>
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-line"></i>
                  <span>PDF</span>
                </>
              )}
            </button>
            <button
              onClick={handleClearTimeline}
              className="px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1.5 bg-red-400/20 text-red-400 hover:bg-red-400/30 border border-red-400/30"
              title="Limpiar historial"
            >
              <i className="ri-delete-bin-line"></i>
              <span>Limpiar</span>
            </button>
            <button
              onClick={() => setShowTimeline(false)}
              className="text-gray-600 hover:text-gray-400 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>

        <div className="p-4">
          {/* Summary stats */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {(() => {
              const report = getAnalyticsReport();
              return (
                <>
                  <div className="bg-cyan-400/10 border border-cyan-400/30 rounded-lg p-2 text-center">
                    <i className="ri-cursor-line text-cyan-400 text-lg block mb-1"></i>
                    <div className="text-xs font-bold text-cyan-400">
                      {report.clicks}
                    </div>
                    <div className="text-[9px] text-gray-500">Clics</div>
                  </div>
                  <div className="bg-green-400/10 border border-green-400/30 rounded-lg p-2 text-center">
                    <i className="ri-arrow-down-line text-green-400 text-lg block mb-1"></i>
                    <div className="text-xs font-bold text-green-400">
                      {report.scrollDepth}%
                    </div>
                    <div className="text-[9px] text-gray-500">Scroll</div>
                  </div>
                  <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-2 text-center">
                    <i className="ri-hand-coin-line text-yellow-400 text-lg block mb-1"></i>
                    <div className="text-xs font-bold text-yellow-400">
                      {report.interactions}
                    </div>
                    <div className="text-[9px] text-gray-500">Interacciones</div>
                  </div>
                  <div className="bg-pink-400/10 border border-pink-400/30 rounded-lg p-2 text-center">
                    <i className="ri-timer-line text-pink-400 text-lg block mb-1"></i>
                    <div className="text-xs font-bold text-pink-400">
                      {Math.floor(report.currentTime / 60)}m
                    </div>
                    <div className="text-[9px] text-gray-500">Tiempo</div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Timeline events */}
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-400 via-purple-400 to-pink-400"></div>
            <div className="space-y-3">
              {timelineEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  <i className="ri-time-line text-3xl mb-2 block"></i>
                  <p>No hay eventos registrados aún</p>
                  <p className="text-xs mt-1">
                    Interactúa con el sitio para ver el historial
                  </p>
                </div>
              ) : (
                [...timelineEvents]
                  .reverse()
                  .map((event, index) => (
                    <div
                      key={index}
                      className="relative pl-10 animate-fade-in"
                    >
                      <div
                        className={`absolute left-2.5 top-1 w-3 h-3 rounded-full border-2 ${getEventColorClass(
                          event.color || 'gray'
                        )}`}
                      ></div>
                      <div
                        className={`border rounded-lg p-2.5 ${getEventColorClass(
                          event.color || 'gray'
                        )}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <i
                              className={`${
                                event.icon || 'ri-record-circle-line'
                              } text-sm`}
                            ></i>
                            <span className="text-xs font-semibold">
                              {event.description || 'Evento'}
                            </span>
                          </div>
                          <span className="text-[10px] text-gray-500 whitespace-nowrap">
                            {formatTimestamp(event.timestamp)}
                          </span>
                        </div>
                        {event.data && (
                          <div className="text-[10px] text-gray-400 ml-5">
                            {event.type === 'click' &&
                              event.data.text && (
                                <span>
                                  Texto: {event.data.text.substring(0, 40)}
                                </span>
                              )}
                            {event.type === 'scroll' && (
                              <span>
                                Profundidad:{' '}
                                {event.data.depth}
                                %
                              </span>
                            )}
                            {event.type === 'interaction' &&
                              event.data.name && (
                                <span>Campo: {event.data.name}</span>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>

          <div className="pt-2 mt-3 border-t border-gray-800">
            <div className="flex items-center gap-2 text-[10px] text-gray-600">
              <i className="ri-refresh-line text-purple-400"></i>
              <span>Actualización automática cada segundo</span>
            </div>
          </div>
        </div>
      </div>

      {/* Exploit panel */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`fixed top-0 h-full z-40 bg-gray-950 border-l border-gray-800 shadow-2xl transition-all duration-500 ease-out overflow-y-auto ${
          isOpen && showExploit
            ? 'translate-x-0 opacity-100'
            : 'translate-x-full opacity-0 pointer-events-none'
        }`}
        style={{ right: '340px', width: '420px', maxWidth: '50vw' }}
      >
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-950 z-10">
          <div className="flex items-center gap-2">
            <i className="ri-shield-keyhole-line text-red-400 text-base animate-pulse"></i>
            <div>
              <span className="text-xs font-bold text-red-400 uppercase tracking-wider block">
                Análisis de Vulnerabilidades
              </span>
              <span className="text-[9px] text-gray-500">
                Hacking Ético • Evaluación de Seguridad
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportToPDF}
              disabled={isExportingPDF}
              className={`px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                isExportingPDF
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-red-400/20 text-red-400 hover:bg-red-400/30 border border-red-400/30'
              }`}
            >
              {isExportingPDF ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>...</span>
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-line"></i>
                  <span>PDF</span>
                </>
              )}
            </button>
            <button
              onClick={() => {
                setVulns([]);
                setExploitLoading(true);
                setTimeout(() => {
                  setVulns(getVulnerabilityAssessment());
                  setExploitLoading(false);
                }, 1500);
              }}
              className="px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1.5 bg-orange-400/20 text-orange-400 hover:bg-orange-400/30 border border-orange-400/30"
              title="Re‑escanear"
            >
              <i className="ri-refresh-line"></i>
              <span>Re‑scan</span>
            </button>
            <button
              onClick={() => setShowExploit(false)}
              className="text-gray-600 hover:text-gray-400 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>

        <div className="p-4">
          {exploitLoading ? (
            <div className="flex flex-col items-center gap-3 py-16 justify-center">
              <div className="relative">
                <div className="w-14 h-14 border-4 border-red-400/20 border-t-red-400 rounded-full animate-spin"></div>
                <i className="ri-shield-keyhole-line text-red-400 text-2xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></i>
              </div>
              <div className="text-center">
                <span className="text-sm text-red-400 font-semibold block">
                  Analizando superficie de ataque...
                </span>
                <span className="text-xs text-gray-500">
                  Evaluando vectores de vulnerabilidad
                </span>
              </div>
              <div className="w-48 h-1 bg-gray-800 rounded-full overflow-hidden mt-2">
                <div
                  className="h-full bg-gradient-to-r from-red-500 via-orange-400 to-yellow-400 animate-pulse"
                  style={{ width: '70%' }}
                ></div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Severity summary */}
              <div className="grid grid-cols-5 gap-2 mb-4">
                {['critical', 'high', 'medium', 'low', 'info'].map((sev) => {
                  const count = vulns.filter((v) => v.severity === sev)
                    .length;
                  const colors: Record<string, string> = {
                    critical:
                      'text-red-500 bg-red-500/10 border-red-500/30',
                    high: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
                    medium:
                      'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
                    low: 'text-green-400 bg-green-400/10 border-green-400/30',
                    info: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
                  };
                  return (
                    <div
                      key={sev}
                      className={`rounded-lg p-2 text-center border ${colors[sev]}`}
                    >
                      <div className="text-lg font-bold">{count}</div>
                      <div className="text-[8px] uppercase font-bold">
                        {getSeverityLabel(sev)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Security score */}
              <div className="bg-gray-900/80 rounded-lg p-4 border border-gray-800/80 text-center">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">
                  Puntuación de Seguridad
                </div>
                {(() => {
                  const critical = vulns.filter(
                    (v) => v.severity === 'critical'
                  ).length;
                  const high = vulns.filter((v) => v.severity === 'high')
                    .length;
                  const medium = vulns.filter(
                    (v) => v.severity === 'medium'
                  ).length;
                  const score = Math.max(
                    0,
                    100 - critical * 25 - high * 15 - medium * 5
                  );
                  const color =
                    score >= 70
                      ? 'text-green-400'
                      : score >= 40
                      ? 'text-yellow-400'
                      : 'text-red-400';
                  const label =
                    score >= 70
                      ? 'ACEPTABLE'
                      : score >= 40
                      ? 'MEJORABLE'
                      : 'CRÍTICO';
                  return (
                    <>
                      <div className={`text-4xl font-bold ${color}`}>
                        {score}
                        <span className="text-lg">/100</span>
                      </div>
                      <div className={`text-xs font-bold mt-1 ${color}`}>
                        {label}
                      </div>
                      <div className="w-full h-2 bg-gray-800 rounded-full mt-3 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-1000 ${
                            score >= 70
                              ? 'bg-green-400'
                              : score >= 40
                              ? 'bg-yellow-400'
                              : 'bg-red-400'
                          }`}
                          style={{ width: `${score}%` }}
                        ></div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Vulnerability list */}
              <div className="space-y-2">
                {vulns.map((v, i) => (
                  <div
                    key={i}
                    className={`rounded-lg p-3 border ${getSeverityColor(
                      v.severity
                    )}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold">{v.name}</span>
                      <span
                        className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${getSeverityColor(
                          v.severity
                        )}`}
                      >
                        {getSeverityLabel(v.severity)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-[10px] font-semibold ${
                          v.status === 'VULNERABLE' ||
                          v.status === 'EXPUESTO'
                            ? 'text-red-400'
                            : v.status === 'RIESGO'
                            ? 'text-orange-400'
                            : 'text-green-400'
                        }`}
                      >
                        {v.status === 'VULNERABLE' ||
                        v.status === 'EXPUESTO'
                          ? '⚠️'
                          : v.status === 'RIESGO'
                          ? '⚠'
                          : '✅'}{' '}
                        {v.status}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400">{v.detail}</p>
                  </div>
                ))}
              </div>

              {/* Disclaimer */}
              <div className="pt-3 mt-2 border-t border-gray-800">
                <div className="bg-gray-900/80 rounded-lg p-3 border border-red-400/20">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-error-warning-line text-red-400 text-sm"></i>
                    <span className="text-[10px] font-bold text-red-400 uppercase">
                      Disclaimer
                    </span>
                  </div>
                  <p className="text-[9px] text-gray-500 leading-relaxed">
                    Este análisis se realiza exclusivamente desde el lado del
                    cliente (navegador) con fines educativos y de auditoría de
                    seguridad. No se realizan ataques ni se explotan
                    vulnerabilidades. Todas las verificaciones son pasivas y no
                    intrusivas.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-red-400 mt-2">
                  <i className="ri-shield-keyhole-line"></i>
                  <span>
                    STI • Análisis de Seguridad Ético • {vulns.length} vectores
                    evaluados
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
